import { apiClient } from './api';

export interface Subscription {
  id: string;
  tenantId: string;
  tenantName: string;
  plan: 'lite' | 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'canceled' | 'past_due' | 'pending';
  billingCycle: 'monthly' | 'yearly';
  price: number;
  startDate: string;
  endDate: string;
  nextBillingDate: string;
  paymentMethod?: string;
  autoRenew: boolean;
}

export interface Invoice {
  id: string;
  tenantId: string;
  tenantName: string;
  subscriptionId: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed' | 'refunded';
  issuedAt: string;
  paidAt?: string;
  dueDate: string;
  failureReason?: string;
  paymentMethod?: 'vnpay' | 'momo' | 'bank_transfer' | 'other';
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
}

export interface BillingOverview {
  totalRevenue: number;
  mrr: number; // Monthly Recurring Revenue
  arr: number; // Annual Recurring Revenue
  activeSubscriptions: number;
  trialSubscriptions: number;
  pastDueSubscriptions: number;
  revenueByPlan: Array<{
    plan: string;
    revenue: number;
    count: number;
    percentage: number;
  }>;
  recentInvoices: Invoice[];
}

export interface SubscriptionFilters {
  status?: string;
  plan?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface SubscriptionsResponse {
  data: Subscription[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface InvoicesResponse {
  data: Invoice[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const unwrapEnvelope = <T>(payload: any): T => {
  if (payload?.data !== undefined) {
    return payload.data as T;
  }
  return payload as T;
};

const normalizePaged = <T>(payload: any): { data: T[]; meta: SubscriptionsResponse['meta'] } => {
  const root = unwrapEnvelope<any>(payload);
  if (Array.isArray(root?.data) && root?.meta) {
    return {
      data: root.data,
      meta: root.meta,
    };
  }

  const items = root?.items ?? root?.data ?? [];
  const meta = root?.meta ?? {
    page: 1,
    limit: Array.isArray(items) ? items.length : 0,
    total: Array.isArray(items) ? items.length : 0,
    totalPages: 1,
  };

  return {
    data: Array.isArray(items) ? items : [],
    meta,
  };
};

const mapMrrPayloadToOverview = (payload: any): BillingOverview => {
  if (
    payload &&
    (payload.mrr !== undefined || payload.arr !== undefined || payload.activeSubscriptions !== undefined)
  ) {
    return payload as BillingOverview;
  }

  return {
    totalRevenue: payload?.currentMrr ?? 0,
    mrr: payload?.currentMrr ?? 0,
    arr: payload?.projectedAnnual ?? 0,
    activeSubscriptions: payload?.activeSubscriptions ?? 0,
    trialSubscriptions: payload?.trialSubscriptions ?? 0,
    pastDueSubscriptions: payload?.pastDueSubscriptions ?? payload?.churnedLastMonth ?? 0,
    revenueByPlan: (payload?.byPlan ?? []).map((item: any) => ({
      plan: item.plan,
      revenue: item.mrr ?? item.revenue ?? 0,
      count: item.tenants ?? item.count ?? 0,
      percentage: item.percentage ?? 0,
    })),
    recentInvoices: payload?.recentInvoices ?? [],
  };
};

export const billingService = {
  getOverview: async (): Promise<BillingOverview> => {
    try {
      const response = await apiClient.get('/billing/overview');
      const payload = unwrapEnvelope<any>(response.data);
      return mapMrrPayloadToOverview(payload);
    } catch (error: any) {
      throw error;
    }
  },

  getSubscriptions: async (filters: SubscriptionFilters = {}): Promise<SubscriptionsResponse> => {
    // Subscriptions come from plans API, not billing
    const response = await apiClient.get('/plans', { params: filters });
    return normalizePaged<Subscription>(response.data);
  },

  getSubscriptionById: async (id: string): Promise<Subscription> => {
    const response = await apiClient.get(`/plans/${id}`);
    return response.data.data;
  },

  updateSubscription: async (id: string, data: Partial<Subscription>): Promise<Subscription> => {
    const response = await apiClient.put(`/plans/${id}`, data);
    return response.data.data;
  },

  cancelSubscription: async (id: string): Promise<void> => {
    // Use deactivate endpoint instead
    await apiClient.put(`/plans/${id}/deactivate`);
  },

  getInvoices: async (filters: { tenantId?: string; status?: string; page?: number; limit?: number } = {}): Promise<InvoicesResponse> => {
    const response = await apiClient.get('/billing/payments', { params: filters });
    return normalizePaged<Invoice>(response.data);
  },

  getInvoiceById: async (id: string): Promise<Invoice> => {
    const response = await apiClient.get(`/billing/payments/${id}`);
    return response.data.data;
  },

  refundInvoice: async (id: string, amount?: number): Promise<void> => {
    await apiClient.post(`/billing/invoices/${id}/refund`, { amount });
  },

  getRevenueReport: async (params: { startDate: string; endDate: string; groupBy: 'day' | 'week' | 'month' }): Promise<{
    data: Array<{
      period: string;
      revenue: number;
      newSubscriptions: number;
      churnedSubscriptions: number;
    }>;
  }> => {
    const response = await apiClient.get('/billing/reports/revenue', { params });
    return unwrapEnvelope(response.data);
  },

  // New methods matching BE endpoints
  getPayments: async (filters: { page?: number; limit?: number; tenantId?: string; status?: string } = {}): Promise<InvoicesResponse> => {
    const response = await apiClient.get('/billing/payments', { params: filters });
    return normalizePaged<Invoice>(response.data);
  },

  getFailedPayments: async (page = 1, limit = 20): Promise<InvoicesResponse> => {
    const response = await apiClient.get('/billing/failed', { params: { page, limit } });
    return normalizePaged<Invoice>(response.data);
  },
};

export default billingService;
